import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '../Common/Loading';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface ExpiryLog {
    ModifiedBy: string;
    ModifiedDate: string;
    PreviousDate: string;
}

const styles = {
    pageContainer: {
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: '10pt',
        padding: '10px',
        backgroundColor: 'White',
        minHeight: '100vh',
        boxSizing: 'border-box' as const
    },
    messageLabel: {
        fontWeight: 'bold',
        color: '#333333',
        marginBottom: '10px',
        display: 'block'
    },
    gridContainer: {
        width: '100%',
        maxHeight: 'calc(100vh - 40px)',
        overflow: 'auto',
        border: '1px solid black',
        backgroundColor: 'White'
    },
    table: {
        width: '100%',
        tableLayout: 'fixed' as const,
        borderCollapse: 'collapse' as const,
        textAlign: 'left' as const,
        color: '#333333'
    },
    th: {
        backgroundColor: '#5D7B9D',
        color: 'White',
        fontWeight: 'bold',
        padding: '6px 4px',
        border: '1px solid White',
        position: 'sticky' as const,
        top: 0,
        zIndex: 1
    },
    td: {
        padding: '4px',
        borderBottom: '1px solid #cccccc',
        verticalAlign: 'middle' as const,
        wordWrap: 'break-word' as const
    },
    rowNormal: {
        backgroundColor: '#F7F6F3',
        color: '#333333'
    },
    rowAlternate: {
        backgroundColor: 'White',
        color: '#284775'
    }
};

const ExpirydateModifiedlog: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [logs, setLogs] = useState<ExpiryLog[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [message, setMessage] = useState<string>('');

    const schoolCode = searchParams.get('SchoolCode') || '';
    const familyCode = searchParams.get('FamilyCode') || searchParams.get('Login') || '';

    useEffect(() => {
        if (schoolCode && familyCode) {
            fetchLogs();
        } else {
            setMessage('Missing required parameters (SchoolCode, FamilyCode/Login).');
            setLoading(false);
        }
    }, [schoolCode, familyCode]);

    const fetchLogs = async () => {
        setLoading(true);
        setMessage('');
        
        try {
            const response = await fetch(`${API_BASE_URL}api/expiry-logs/${schoolCode}/${familyCode}/`);
            
            if (!response.ok) {
                throw new Error('Failed to fetch logs');
            }

            const data = await response.json();
            
            if (data.error) {
                setMessage(data.error);
            } else {
                setLogs(data || []);
            }
        } catch (error) {
            console.error('Error fetching logs:', error);
            setMessage('An error occurred while fetching the logs.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        return dateString;
    };

    if (loading) {
        return (
            <div style={styles.pageContainer}>
                <Loading />
            </div>
        );
    }

    return (
        <div style={styles.pageContainer}>
            {message && <span style={styles.messageLabel}>{message}</span>}
            
            {!message && (
                <div style={styles.gridContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, width: '30%' }}>Modified By</th>
                                <th style={{ ...styles.th, width: '35%' }}>Modified Date</th>
                                <th style={{ ...styles.th, width: '35%' }}>Previous Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map((log, index) => {
                                const isAlternate = index % 2 !== 0;
                                const rowStyle = isAlternate ? styles.rowAlternate : styles.rowNormal;
                                
                                return (
                                    <tr key={index} style={rowStyle}>
                                        <td style={styles.td}>{log.ModifiedBy}</td>
                                        <td style={styles.td}>{formatDate(log.ModifiedDate)}</td>
                                        <td style={styles.td}>{log.PreviousDate ? new Date(log.PreviousDate).toLocaleDateString() : '-'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ExpirydateModifiedlog;